import React, { useState } from 'react';
import { X, Download, Database, ArrowRight, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { propertyService } from '../../../lib/supabase';

export default function BulkOperations({ communes, properties, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('migrate');
  const [migrating, setMigrating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState(null);

  const [migrationSettings, setMigrationSettings] = useState({
    sourceCommune: '',
    targetCommune: '',
    propertyTypes: [],
    statusFilter: 'all'
  });

  const [exportSettings, setExportSettings] = useState({
    format: 'csv',
    includeCommunes: true,
    includeProperties: true,
    dateRange: 'all'
  });

  const handlePropertyMigration = async () => {
    if (!migrationSettings?.sourceCommune || !migrationSettings?.targetCommune) {
      setResults({ error: 'Sélectionnez les communes source et cible' });
      return;
    }

    if (migrationSettings?.sourceCommune === migrationSettings?.targetCommune) {
      setResults({ error: 'Les communes source et cible doivent être différentes' });
      return;
    }

    setMigrating(true);
    setResults(null);

    try {
      // Get properties to migrate
      const sourceProperties = properties?.filter(property => {
        if (property?.commune_id !== migrationSettings?.sourceCommune) return false;
        
        if (migrationSettings?.statusFilter !== 'all' && property?.status !== migrationSettings?.statusFilter) {
          return false;
        }
        
        if (migrationSettings?.propertyTypes?.length > 0 && 
            !migrationSettings?.propertyTypes?.includes(property?.property_type)) {
          return false;
        }
        
        return true;
      }) || [];

      if (sourceProperties?.length === 0) {
        setResults({ error: 'Aucune propriété correspondante trouvée pour la migration' });
        return;
      }

      // Migrate properties
      let migrated = 0;
      let errors = [];

      for (const property of sourceProperties) {
        try {
          const { error } = await propertyService?.updateProperty(property?.id, {
            commune_id: migrationSettings?.targetCommune
          });
          
          if (error) {
            errors?.push(`${property?.title}: ${error}`);
          } else {
            migrated++;
          }
        } catch (err) {
          errors?.push(`${property?.title}: ${err?.message}`);
        }
      }

      setResults({
        success: true,
        migrated,
        total: sourceProperties?.length,
        errors: errors?.slice(0, 10) // Show first 10 errors
      });

      // Refresh data
      await onRefresh();

    } catch (error) {
      setResults({ error: error?.message || 'Erreur lors de la migration' });
    } finally {
      setMigrating(false);
    }
  };

  const handleDataExport = async () => {
    setExporting(true);
    setResults(null);

    try {
      const exportData = {
        metadata: {
          exportDate: new Date()?.toISOString(),
          format: exportSettings?.format,
          totalCommunes: communes?.length || 0,
          totalProperties: properties?.length || 0
        },
        communes: exportSettings?.includeCommunes ? communes?.map(commune => ({
          id: commune?.id,
          name: commune?.name,
          district: commune?.district,
          description: commune?.description,
          population: commune?.population,
          average_price: commune?.average_price,
          amenities: commune?.amenities,
          property_count: properties?.filter(p => p?.commune_id === commune?.id)?.length || 0
        })) : [],
        properties: exportSettings?.includeProperties ? properties?.map(property => ({
          id: property?.id,
          title: property?.title,
          address: property?.address,
          property_type: property?.property_type,
          status: property?.status,
          price: property?.price,
          surface_area: property?.surface_area,
          bedrooms: property?.bedrooms,
          bathrooms: property?.bathrooms,
          commune_name: communes?.find(c => c?.id === property?.commune_id)?.name || 'N/A',
          commune_district: communes?.find(c => c?.id === property?.commune_id)?.district || 'N/A',
          agent_name: property?.agent?.full_name || 'N/A',
          owner_name: property?.owner?.full_name || 'N/A',
          created_at: property?.created_at,
          features: property?.features
        })) : []
      };

      // Generate downloadable file
      let content, filename, mimeType;

      if (exportSettings?.format === 'json') {
        content = JSON.stringify(exportData, null, 2);
        filename = `data_export_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
        mimeType = 'application/json';
      } else {
        // CSV format
        const csvData = [];
        
        if (exportSettings?.includeCommunes) {
          csvData?.push('=== COMMUNES ===');
          csvData?.push('ID,Nom,District,Population,Prix Moyen,Nombre Propriétés,Description');
          
          exportData?.communes?.forEach(commune => {
            csvData?.push([
              commune?.id,
              `"${commune?.name}"`,
              `"${commune?.district}"`,
              commune?.population || '',
              commune?.average_price || '',
              commune?.property_count,
              `"${(commune?.description || '')?.replace(/"/g, '""')}"`
            ]?.join(','));
          });
          
          csvData?.push('');
        }

        if (exportSettings?.includeProperties) {
          csvData?.push('=== PROPRIÉTÉS ===');
          csvData?.push('ID,Titre,Adresse,Type,Statut,Prix,Surface,Chambres,SdB,Commune,District,Agent,Propriétaire,Date Création');
          
          exportData?.properties?.forEach(property => {
            csvData?.push([
              property?.id,
              `"${property?.title}"`,
              `"${property?.address}"`,
              property?.property_type,
              property?.status,
              property?.price,
              property?.surface_area || '',
              property?.bedrooms || '',
              property?.bathrooms || '',
              `"${property?.commune_name}"`,
              `"${property?.commune_district}"`,
              `"${property?.agent_name}"`,
              `"${property?.owner_name}"`,
              property?.created_at?.split('T')?.[0] || ''
            ]?.join(','));
          });
        }

        content = csvData?.join('\n');
        filename = `data_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
        mimeType = 'text/csv;charset=utf-8;';
      }

      // Download file
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      setResults({
        success: true,
        exported: true,
        filename,
        communes: exportData?.communes?.length,
        properties: exportData?.properties?.length
      });

    } catch (error) {
      setResults({ error: error?.message || 'Erreur lors de l\'export' });
    } finally {
      setExporting(false);
    }
  };

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <Database className="w-6 h-6 mr-2 text-blue-600" />
            Opérations Groupées
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              className={`px-6 py-3 font-medium text-sm border-b-2 ${
                activeTab === 'migrate' ?'border-blue-600 text-blue-600 bg-blue-50' :'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('migrate')}
            >
              <ArrowRight className="w-4 h-4 inline mr-2" />
              Migration Propriétés
            </button>
            
            <button
              className={`px-6 py-3 font-medium text-sm border-b-2 ${
                activeTab === 'export'
                  ? 'border-blue-600 text-blue-600 bg-blue-50' :'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('export')}
            >
              <Download className="w-4 h-4 inline mr-2" />
              Export Données
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Migration Tab */}
          {activeTab === 'migrate' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-yellow-800">Attention</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Cette opération déplacera les propriétés sélectionnées d'une commune à une autre. 
                      Cette action est irréversible.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commune source *
                  </label>
                  <Select
                    value={migrationSettings?.sourceCommune}
                    onChange={(e) => setMigrationSettings(prev => ({
                      ...prev,
                      sourceCommune: e?.target?.value
                    }))}
                  >
                    <option value="">Sélectionner commune source</option>
                    {communes?.map((commune) => (
                      <option key={commune?.id} value={commune?.id}>
                        {commune?.name} - {commune?.district} ({properties?.filter(p => p?.commune_id === commune?.id)?.length || 0} propriétés)
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commune cible *
                  </label>
                  <Select
                    value={migrationSettings?.targetCommune}
                    onChange={(e) => setMigrationSettings(prev => ({
                      ...prev,
                      targetCommune: e?.target?.value
                    }))}
                  >
                    <option value="">Sélectionner commune cible</option>
                    {communes?.filter(c => c?.id !== migrationSettings?.sourceCommune)?.map((commune) => (
                      <option key={commune?.id} value={commune?.id}>
                        {commune?.name} - {commune?.district}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filtrer par statut
                  </label>
                  <Select
                    value={migrationSettings?.statusFilter}
                    onChange={(e) => setMigrationSettings(prev => ({
                      ...prev,
                      statusFilter: e?.target?.value
                    }))}
                  >
                    {statusOptions?.map((status) => (
                      <option key={status?.value} value={status?.value}>
                        {status?.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Types de propriétés (optionnel)
                  </label>
                  <div className="space-y-2">
                    {propertyTypes?.map((type) => (
                      <label key={type?.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={migrationSettings?.propertyTypes?.includes(type?.value)}
                          onChange={(e) => {
                            if (e?.target?.checked) {
                              setMigrationSettings(prev => ({
                                ...prev,
                                propertyTypes: [...prev?.propertyTypes, type?.value]
                              }));
                            } else {
                              setMigrationSettings(prev => ({
                                ...prev,
                                propertyTypes: prev?.propertyTypes?.filter(t => t !== type?.value)
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{type?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePropertyMigration}
                disabled={migrating || !migrationSettings?.sourceCommune || !migrationSettings?.targetCommune}
                className="w-full flex items-center justify-center"
              >
                {migrating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Migrer les Propriétés
              </Button>
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800">Export des Données</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Exportez vos données pour sauvegarde, analyse ou transfert vers d'autres systèmes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Format d'export
                  </label>
                  <Select
                    value={exportSettings?.format}
                    onChange={(e) => setExportSettings(prev => ({
                      ...prev,
                      format: e?.target?.value
                    }))}
                  >
                    <option value="csv">CSV (Excel)</option>
                    <option value="json">JSON</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Données à inclure
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exportSettings?.includeCommunes}
                        onChange={(e) => setExportSettings(prev => ({
                          ...prev,
                          includeCommunes: e?.target?.checked
                        }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Communes ({communes?.length || 0})</span>
                    </label>
                    
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exportSettings?.includeProperties}
                        onChange={(e) => setExportSettings(prev => ({
                          ...prev,
                          includeProperties: e?.target?.checked
                        }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Propriétés ({properties?.length || 0})</span>
                    </label>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleDataExport}
                disabled={exporting || (!exportSettings?.includeCommunes && !exportSettings?.includeProperties)}
                className="w-full flex items-center justify-center"
              >
                {exporting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exporter les Données
              </Button>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="mt-6">
              {results?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Erreur</h4>
                      <p className="text-sm text-red-700 mt-1">{results?.error}</p>
                    </div>
                  </div>
                </div>
              ) : results?.success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-800">
                        {activeTab === 'migrate' ? 'Migration Réussie' : 'Export Réussi'}
                      </h4>
                      
                      {activeTab === 'migrate' && (
                        <div className="text-sm text-green-700 mt-1">
                          <p>{results?.migrated} propriété(s) migrée(s) sur {results?.total}</p>
                          {results?.errors?.length > 0 && (
                            <div className="mt-2">
                              <p className="font-medium">Erreurs:</p>
                              <ul className="list-disc list-inside">
                                {results?.errors?.map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {activeTab === 'export' && (
                        <div className="text-sm text-green-700 mt-1">
                          <p>Fichier téléchargé: {results?.filename}</p>
                          <p>{results?.communes} commune(s) et {results?.properties} propriété(s) exportées</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t">
          <Button onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}